import React, { useEffect, useRef, useState } from "react";
import { __ } from "@wordpress/i18n";

const VideoComponent = ({ post, playerExperience, dynamicHeight }) => {
  const [currentPost, setCurrentPost] = useState(post);
  const videoRef = useRef(null);

  useEffect(() => {
    setCurrentPost(post);
  }, [post]);

  // a11y(SMASH-1377 F4/F5): inline playback replaces the focused thumbnail /
  // gallery play-trigger <button> with this <video>, dropping keyboard focus
  // to <body> (WCAG 2.4.3). Inline mounts (and gallery post switches) are
  // always user-initiated, so move focus to the video. The lightbox player
  // keeps Radix Dialog's own focus management (playerExperience defaults to
  // 'lightbox' there).
  useEffect(() => {
    if (playerExperience !== "inline") return;
    const video = videoRef.current;
    if (!video) return;
    video.focus();
    if (document.activeElement !== video) {
      // Some engines don't make <video controls> itself focusable by default.
      video.setAttribute("tabindex", "-1");
      video.focus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerExperience, post?.id]);

  // Determine video styles based on playerExperience and dynamicHeight
  const videoStyles = () => {
    let height = dynamicHeight || 'inherit';

    switch (playerExperience) {
      case "inline":
        return {
          height: height, // Set the height based on dynamicHeight
          width: "100%", // Inherit the parent's width
          backgroundColor: "black",
        };
      case "lightbox":
      default:
        return {
          height: "80vh",
          width: "100%",
        };
    }
  };

  // Determine video behavior based on playerExperience
  const videoProps = () => {
    switch (playerExperience) {
      case "inline":
        return {
          autoPlay: true,
        };
      case "lightbox":
      default:
        return {
          autoPlay: true,
          loop: true,
        };
    }
  };

  if (currentPost.id !== post.id) {
    return "";
  }

  // Closes finding v3 S10: <video> previously had no accessible name.
  // Use the post caption when available so AT users get parity with
  // sighted users; fall back to a generic localized label otherwise.
  const postCaption = currentPost?.video_description;
  const ariaLabel = postCaption || __("Video", "sb-customizer");

  return (
    <video
      {...videoProps()}
      controls
      ref={videoRef}
      style={videoStyles()}
      aria-label={ariaLabel}
    >
      <source src={currentPost?.video_url ?? ""} type="video/mp4" />
      {__("Your browser does not support the video tag.", "sb-customizer")}
    </video>
  );
};

export default VideoComponent;
